import { GoogleLogin, GoogleOAuthProvider } from '@react-oauth/google';
import { useAuth } from '../contexts/AuthContext';
import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Box, 
  Paper, 
  Typography, 
  Container, 
  useTheme, 
  alpha, 
  Divider,
  keyframes,
  TextField,
  Button,
  CircularProgress,
  Alert,
  Stack,
  Tab,
  Tabs,
  Link
} from '@mui/material';
import EmailIcon from '@mui/icons-material/Email';
import cribCriticLogo from '../assets/CribCriticLogo.svg';

function LoginPage() {
  const { login, isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const theme = useTheme();
  
  // Email login states
  const [email, setEmail] = useState('');
  const [pin, setPin] = useState('');
  const [step, setStep] = useState<'email' | 'pin'>('email');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [loginMethod, setLoginMethod] = useState<'google' | 'email'>('google');

  useEffect(() => {
    if (isAuthenticated) {
      navigate('/homes');
    }
  }, [isAuthenticated, navigate]);

  const handleSuccess = (credentialResponse: any) => {
    login(credentialResponse);
  };

  const handleEmailSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setLoading(true);
    
    // Normalize email to lowercase for case-insensitive matching
    const normalizedEmail = email.toLowerCase().trim();
    
    try {
      const response = await fetch(`/api/account/send_challenge?email=${encodeURIComponent(normalizedEmail)}`);
      const data = await response.json();
      
      if (data.success) {
        setStep('pin');
      } else {
        setError(data.error || 'Failed to send login PIN. Please try again.');
      }
    } catch (err) {
      setError('Network error. Please check your connection and try again.');
    } finally {
      setLoading(false);
    }
  };

  const handlePinSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setLoading(true);
    
    // Normalize email to lowercase for case-insensitive matching
    const normalizedEmail = email.toLowerCase().trim();
    
    try {
      const response = await fetch(`/api/account/login?email=${encodeURIComponent(normalizedEmail)}&pin=${encodeURIComponent(pin)}`);
      const data = await response.json();
      
      if (data.success) {
        // Reload the page to initialize app with the new auth cookie
        window.location.href = '/homes';
      } else {
        setError(data.error || 'Invalid PIN. Please try again.');
      }
    } catch (err) {
      setError('Network error. Please check your connection and try again.');
    } finally {
      setLoading(false);
    }
  };

  const subtleFloat = keyframes`
    0% { transform: translateY(0px); }
    50% { transform: translateY(-5px); }
    100% { transform: translateY(0px); }
  `;

  return (
    <Box sx={{
      minHeight: '100vh',
      display: 'flex',
      background: `linear-gradient(145deg, ${alpha(theme.palette.background.default, 0.8)} 0%, ${alpha(theme.palette.background.paper, 0.9)} 100%)`,
      position: 'relative',
      overflow: 'hidden',
      '&::before': {
        content: '""',
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundImage: 'radial-gradient(circle at 20% 35%, rgba(76, 175, 80, 0.04) 0%, transparent 50%), radial-gradient(circle at 75% 80%, rgba(33, 150, 243, 0.04) 0%, transparent 50%)',
        zIndex: 0,
      }
    }}>
      <Container maxWidth="sm" sx={{ 
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        position: 'relative',
        zIndex: 1,
        py: 4
      }}>
        <Paper elevation={0} sx={{ 
          p: { xs: 3, sm: 5 }, 
          borderRadius: 3,
          width: '100%',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: 4,
          backgroundColor: alpha('#ffffff', 0.9),
          backdropFilter: 'blur(8px)',
          boxShadow: `0 10px 40px -10px ${alpha(theme.palette.primary.main, 0.2)}, 
                      0 2px 10px -3px ${alpha(theme.palette.common.black, 0.1)}`,
          border: `1px solid ${alpha(theme.palette.common.white, 0.7)}`
        }}>
          {/* Logo and App Name */}
          <Box sx={{ 
            display: 'flex', 
            flexDirection: 'column',
            alignItems: 'center',
            gap: 2,
            animation: `${subtleFloat} 6s ease-in-out infinite`
          }}>
            <Box sx={{
              p: 2.5,
              borderRadius: '50%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              mb: 1,
              boxShadow: `0 8px 20px -8px ${alpha(theme.palette.primary.main, 0.5)}`,
              background: 'transparent'
            }}>
              <img 
                src={cribCriticLogo} 
                alt="CribCritic Logo" 
                style={{ 
                  width: 80,
                  height: 80,
                  objectFit: 'contain'
                }} 
              />
            </Box>
            
            <Typography 
              variant="h4" 
              component="h1" 
              sx={{ 
                fontWeight: 800, 
                backgroundImage: `linear-gradient(135deg, ${theme.palette.primary.dark} 0%, ${theme.palette.primary.main} 100%)`,
                backgroundClip: 'text',
                color: 'transparent',
                letterSpacing: '-0.01em'
              }}
            >
              CribCritic
            </Typography>
            
            <Typography 
              variant="body1" 
              color="text.secondary" 
              sx={{ 
                textAlign: 'center', 
                maxWidth: '90%',
                fontWeight: 500
              }}
            >
              Your personal AI assistant for evaluating homes and properties
            </Typography>
          </Box>

          <Divider flexItem sx={{ 
            opacity: 0.6,
            '&::before, &::after': {
              borderColor: alpha(theme.palette.divider, 0.08),
            }
          }} />

          {/* Login Section */}
          <Box sx={{ 
            width: '100%', 
            display: 'flex', 
            flexDirection: 'column', 
            alignItems: 'center', 
            gap: 3.5 
          }}>
            <Typography 
              variant="h6" 
              sx={{ 
                fontWeight: 600,
                color: theme.palette.text.primary,
                letterSpacing: '0.02em'
              }}
            >
              Sign in to continue
            </Typography>
            
            <Typography 
              variant="body2" 
              sx={{ 
                color: alpha(theme.palette.text.secondary, 0.9),
                textAlign: 'center',
                mb: 1,
                maxWidth: '90%'
              }}
            >
              No registration needed! Simply sign in with Google or email to create your account instantly.
            </Typography>
            
            {/* Login Method Tabs */}
            <Tabs
              value={loginMethod}
              onChange={(_, newValue) => setLoginMethod(newValue)}
              sx={{ mb: 1 }}
            >
              <Tab label="Google" value="google" />
              <Tab label="Email" value="email" />
            </Tabs>

            {/* Google Login - now with GoogleOAuthProvider */}
            {loginMethod === 'google' && (
              <GoogleOAuthProvider clientId="30446173137-uqc22bapg1f6h23ipv2mk16ov8jpv51j.apps.googleusercontent.com">
                <Box sx={{ 
                  width: '100%', 
                  maxWidth: 300, 
                  display: 'flex',
                  justifyContent: 'center',
                  '& > div': { 
                    width: '100%',
                    transition: 'transform 0.2s ease-in-out',
                    '&:hover': {
                      transform: 'scale(1.02)'
                    }
                  }
                }}>
                  <GoogleLogin
                    onSuccess={handleSuccess}
                    onError={() => console.log('Login Failed')}
                    theme="outline"
                    size="large"
                    shape="rectangular"
                    text="signin_with"
                    locale="en"
                  />
                </Box>
              </GoogleOAuthProvider>
            )}

            {/* Email Login */}
            {loginMethod === 'email' && (
              <Box sx={{ width: '100%', maxWidth: 300 }}>
                {error && (
                  <Alert severity="error" sx={{ mb: 2 }}>
                    {error}
                  </Alert>
                )}
                
                {step === 'email' ? (
                  <Stack component="form" onSubmit={handleEmailSubmit} spacing={2}>
                    <TextField
                      label="Email Address"
                      type="email"
                      fullWidth
                      required
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      disabled={loading}
                      InputProps={{
                        startAdornment: <EmailIcon sx={{ color: 'action.active', mr: 1 }} />,
                      }}
                    />
                    <Button
                      type="submit"
                      variant="contained"
                      fullWidth
                      disabled={loading}
                      sx={{
                        py: 1.5,
                        fontWeight: 600,
                        transition: 'all 0.2s ease-in-out',
                        '&:hover': {
                          transform: 'translateY(-2px)',
                          boxShadow: `0 6px 20px -5px ${alpha(theme.palette.primary.main, 0.4)}`
                        }
                      }}
                    >
                      {loading ? <CircularProgress size={24} /> : 'Send Login PIN'}
                    </Button>
                  </Stack>
                ) : (
                  <Stack component="form" onSubmit={handlePinSubmit} spacing={2}>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1, textAlign: 'center' }}>
                      We sent a login PIN to <strong>{email}</strong>
                    </Typography>
                    <TextField
                      label="Enter PIN"
                      fullWidth
                      required
                      value={pin}
                      onChange={(e) => setPin(e.target.value)}
                      disabled={loading}
                      placeholder="Enter the PIN from your email"
                    />
                    <Button
                      type="submit"
                      variant="contained"
                      fullWidth
                      disabled={loading}
                      sx={{
                        py: 1.5,
                        fontWeight: 600,
                        transition: 'all 0.2s ease-in-out',
                        '&:hover': {
                          transform: 'translateY(-2px)',
                          boxShadow: `0 6px 20px -5px ${alpha(theme.palette.primary.main, 0.4)}`
                        }
                      }}
                    >
                      {loading ? <CircularProgress size={24} /> : 'Verify PIN'}
                    </Button>
                    <Button
                      variant="text"
                      onClick={() => {
                        setStep('email');
                        setPin('');
                        setError(null);
                      }}
                      sx={{ fontWeight: 500 }}
                    >
                      Try Different Email
                    </Button>
                  </Stack>
                )}
              </Box>
            )}
            
            <Typography 
              variant="body2" 
              sx={{ 
                color: alpha(theme.palette.text.secondary, 0.8),
                textAlign: 'center',
                fontSize: '0.8rem',
                maxWidth: '80%'
              }}
            >
              By signing in, you agree to our <Link href="/terms-of-service" sx={{ color: theme.palette.primary.main, fontWeight: 500 }}>Terms of Service</Link> and <Link href="/privacy-policy" sx={{ color: theme.palette.primary.main, fontWeight: 500 }}>Privacy Policy</Link>
            </Typography>
          </Box>
        </Paper>
      </Container>
    </Box>
  );
}

export default LoginPage;
